import axios, { type AxiosError } from 'axios';

import { HttpError } from '../../../utilities/errors/HTTPErrors';
import { isTransientError } from '../../../utilities/errors/isTransientError';
import type { NetworkErrorType } from '../../../utilities/errors/networkErrorType';

import { getNetworkErrorMessage } from './errorMessages';

function isBrowserOffline(): boolean {
  return typeof navigator !== 'undefined' && !navigator.onLine;
}

function networkTypeFromHttpError(error: HttpError): NetworkErrorType {
  switch (error.name) {
    case 'NetworkError':
      return isBrowserOffline() ? 'offline' : 'server_unreachable';
    case 'TimeoutError':
      return 'timeout';
    case 'BadGateway':
    case 'BadResponseError':
    case 'ServiceUnavailable':
    case 'GatewayTimeout':
      return 'server_error';
    default:
      if (
        typeof error.code === 'number' &&
        error.code >= 500 &&
        error.code <= 599
      ) {
        return 'server_error';
      }
      if (isTransientError(error)) {
        return 'server_unreachable';
      }
      return 'unknown';
  }
}

/**
 * Classifies an axios or HTTP error into a {@link NetworkErrorType} for UX and retry logic.
 */
export function classifyNetworkError(error: unknown): NetworkErrorType {
  if (isBrowserOffline()) {
    return 'offline';
  }

  if (error instanceof HttpError) {
    return networkTypeFromHttpError(error);
  }

  if (!axios.isAxiosError(error)) {
    return 'unknown';
  }

  const axiosError = error as AxiosError;

  if (axios.isCancel(axiosError) || axiosError.code === 'ERR_CANCELED') {
    return 'unknown';
  }

  const status = axiosError.response?.status;
  if (typeof status === 'number' && status >= 500 && status <= 599) {
    return 'server_error';
  }

  switch (axiosError.code) {
    case 'ECONNABORTED':
    case 'ETIMEDOUT':
      return 'timeout';
    case 'ERR_NETWORK':
      return isBrowserOffline() ? 'offline' : 'server_unreachable';
    default:
      break;
  }

  if (isTransientError(axiosError)) {
    return 'server_unreachable';
  }

  return 'unknown';
}

/**
 * Maps any error value to a user-facing network message.
 */
export function getNetworkErrorMessageFromUnknown(error: unknown): string {
  return getNetworkErrorMessage(classifyNetworkError(error));
}
